import {useNavigate} from 'react-router-dom';
import ArrowBackIcon from '@app/assets/icons/back.svg?react';
import {BaseDialog, Button} from '@app/components';
import ArrowLeft from '@app/assets/icons/arrow-left.svg?react';
import ArrowRight from '@app/assets/icons/arrow-right.svg?react';
import SettingsIcon from '@app/assets/icons/settings-without-background.svg?react';
import AutomationIcon from '@app/assets/icons/automation.svg?react';
import NewCampaignIcon from '@app/assets/icons/new-campaign.svg?react';
import {CreateAutomation as CreateAutomationRoutes} from '@app/utils/consts';
import {useMemo} from 'react';
import {CreateAutomationSettings} from './CreateAutomationSettings.tsx';
import {CreateAutomationConfigAutomation} from './CreateAutomationConfigAutomation.tsx';
import clsx from 'clsx';
import {useForm} from 'react-hook-form';
import {useFormChangeDetection} from './useFormChangeDetection';
import CloseIcon from '@app/assets/icons/close.svg?react';
import {useNavigationContext} from '@app/contexts/NavigationContext';
import {useAppDispatch} from '@app/redux/hooks';
import {updateFormData} from '@app/redux/features/campaignForm';

export const CreateAutomation = ({ page }: { page: CreateAutomationRoutes }) => {
  const navigate = useNavigate();
  const automationCreationForm = useForm();
  const { interceptNavigation } = useNavigationContext();
  const dispatch = useAppDispatch();

  // Use the custom hook for form change detection
  const {
    showConfirmationModal,
    handleConfirmNavigation,
    handleCancelNavigation,
  } = useFormChangeDetection(automationCreationForm);

  // Check if we're editing a draft automation
  const isEditing = automationCreationForm.watch('automationId');

  const handleNext = () => {
    // Save current form data before navigating
    const values = automationCreationForm.getValues();
    dispatch(updateFormData(values));
    localStorage.setItem('automationFormData', JSON.stringify(values));

    switch (page) {
      case CreateAutomationRoutes.SETTINGS: {
        navigate(`/automations/create/automation`);
        break;
      }
      case CreateAutomationRoutes.AUTOMATION: {
        break;
      }
      default: {
        navigate(`/automations/create/settings`);
      }
    }
  };

  const handlePrevious = () => {
    const values = automationCreationForm.getValues();
    dispatch(updateFormData(values));
    localStorage.setItem('automationFormData', JSON.stringify(values));
    switch (page) {
      case CreateAutomationRoutes.SETTINGS: {
        break;
      }
      case CreateAutomationRoutes.AUTOMATION: {
        navigate(`/automations/create/settings`);
        break;
      }
      default: {
        navigate(`/automations/create/settings`);
      }
    }
  };

  // Handle direct navigation for step buttons
  const handleStepNavigation = (targetPath: string) => {
    const values = automationCreationForm.getValues();
    dispatch(updateFormData(values));
    localStorage.setItem('automationFormData', JSON.stringify(values));
    navigate(targetPath);
  };

  // Handle confirmation modal actions
  const handleConfirmNavigationAction = () => {
    const targetPath = handleConfirmNavigation();
    if (targetPath) {
      navigate(targetPath);
    }
  };

  const renderPage = useMemo(() => {
    switch (page) {
      case CreateAutomationRoutes.SETTINGS: {
        return <CreateAutomationSettings form={automationCreationForm} />;
      }
      case CreateAutomationRoutes.AUTOMATION: {
        return <CreateAutomationConfigAutomation form={automationCreationForm} />;
      }
      default: {
        return <CreateAutomationSettings form={automationCreationForm} />;
      }
    }
  }, [page]);

  return (
    <div className="relative">
      <div className="bg-white border-b border-b-base-ghostWhite template-editor-automation-header">
        <div className="container mx-auto p-4 flex items-center">
          <h2 className="font-bold text-[28px] flex items-center">
            <button
              onClick={() => interceptNavigation('/automations')}
              className="mr-2"
            >
              <ArrowBackIcon />
            </button>
            {isEditing ? 'Edit Automation' : 'Create Automation'}
            <div className="bg-[#F6F7FA] rounded-md p-2 ml-2">
              <NewCampaignIcon />
            </div>
          </h2>
        </div>
        <hr />
        <div className="container mx-auto p-4 flex justify-between items-center">
          <Button
            kind="secondary"
            onClick={handlePrevious}
            className="min-w-fit"
          >
            <ArrowLeft />
          </Button>
          <div className="flex gap-2 items-center">
            <button
              onClick={() => handleStepNavigation(`/automations/create/settings`)}
              className={clsx(
                'border border-base-ghostWhite px-4 py-1 rounded-full text-sm flex items-center gap-2',
                {
                  'border-brand-baseBlue bg-brand-lightBlue4 [&_path]:stroke-brand-dark3': page === CreateAutomationRoutes.SETTINGS,
                }
              )}
            >
              <SettingsIcon width={16} height={16} />
              1. Settings
            </button>
            <ArrowRight />
            <button
              onClick={() => handleStepNavigation(`/automations/create/automation`)}
              className={clsx(
                'border border-base-ghostWhite px-4 py-1 rounded-full text-sm flex items-center gap-2',
                {
                  'border-brand-baseBlue bg-brand-lightBlue4 [&_path]:fill-brand-dark3': page === CreateAutomationRoutes.AUTOMATION,
                }
              )}
              disabled={!automationCreationForm.formState.isValid}
            >
              <AutomationIcon width={16} height={16} />
              2. Automation
            </button>
          </div>
          <Button
            onClick={handleNext}
            className="min-w-fit"
            // disabled={
            //   page === CreateAutomationRoutes.AUTOMATION ||
            //   !automationCreationForm.formState.isValid
            // }
          >
            <ArrowRight className="[&_path]:stroke-[white]" />
          </Button>
        </div>
      </div>
      <div>{renderPage}</div>

      <BaseDialog open={showConfirmationModal} onClose={handleCancelNavigation}>
        <div className="border-b border-b-base-ghostWhite p-6 mb-6 flex gap-2 items-start justify-between">
          <div className="bg-base-antiFlash flex items-center p-2 rounded-lg">
            <CloseIcon width={24} height={24} />
          </div>
          <div className="mr-auto my-auto">
            <h5 className="font-bold text-lg">Unsaved Changes</h5>
          </div>
          <div>
            <button type="button" onClick={handleCancelNavigation}>
              <CloseIcon />
            </button>
          </div>
        </div>
        <div className="px-6 pb-4">
          <p className="text-base-davyGray">
            You have unsaved changes in your automation. Are you sure you want to
            leave this page?
          </p>
          <div className="flex items-center gap-4 justify-center py-4">
            <Button
              kind="secondary"
              className="w-full"
              type="button"
              onClick={handleCancelNavigation}
            >
              Cancel
            </Button>
            <Button
              className="w-full"
              type="button"
              onClick={handleConfirmNavigationAction}
            >
              Leave Page
            </Button>
          </div>
        </div>
      </BaseDialog>
    </div>
  );
};
